import {Component, ElementRef, HostListener, OnDestroy, OnInit} from '@angular/core';
import {DELIVERY_EXPENSES, FREE_DELIVERY_THRESHOLD} from "../../services/util/constants";
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {OrderItem} from "../../models/order-item";
import {Product} from "../../models/product";
import {CartService} from "../cart/service/cart.service";
import {Router} from "@angular/router";
import {OrderSuccessState} from "../order-success/order-success.component";
import {CheckoutService} from "./service/checkout.service";
import {OrderCreationRequest} from "../../models/order";
import {AuthService} from "@auth0/auth0-angular";
import {initTE, Modal, Toast} from "tw-elements";
import {ProfileService} from "../profile/service/profile.service";
import {animate, style, transition, trigger} from "@angular/animations";
import {goToUrl} from "../../services/util/util";
import {CouponCodeRequest} from "../../models/coupon-code-request";
import {Customer} from "../../models/customer";
import {FBQ} from "../../models/fbq";
import {catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, switchMap, takeUntil, tap} from "rxjs";
import {PlaceOption, StreetOption} from "./models/address-option";
import {AnalyticsService} from "../../services/util/analytics.service";

declare let fbq: FBQ;

@Component({
    selector: 'app-checkout',
    templateUrl: './checkout.component.html',
    styleUrls: ['./checkout.component.css'],
    animations: [
        trigger('slideDown', [
            transition(':enter', [
                style({opacity: 0}),
                animate('150ms ease-out')
            ])
        ])]
})
export class CheckoutComponent implements OnInit, OnDestroy {

    customer?: Customer;

    submitted = false;

    order: UntypedFormGroup = new UntypedFormGroup({
        name: new UntypedFormControl('', [Validators.required]),
        surname: new UntypedFormControl('', [Validators.required]),
        phoneNumber: new UntypedFormControl('', [Validators.required]),
        address: new UntypedFormControl('', [Validators.required]),
        city: new UntypedFormControl('', [Validators.required]),
        placeInput: new UntypedFormControl('', [Validators.required]),
        selectedPlaceId: new UntypedFormControl(null, [Validators.required]),
        selectedPlaceEcoId: new UntypedFormControl(null),
        streetInput: new UntypedFormControl({value: '', disabled: true}, [Validators.required]),
        selectedStreetId: new UntypedFormControl(null),
        selectedStreetEcoId: new UntypedFormControl(null),
        manualStreet: new UntypedFormControl(''),
        houseNumber: new UntypedFormControl('', [Validators.required]),
        entrance: new UntypedFormControl(''),
        apartment: new UntypedFormControl(''),
        email: new UntypedFormControl('', [Validators.email]),
        couponCode: new UntypedFormControl(''),
        usePoints: new UntypedFormControl(false)
    });

    orderPrice: number = 0

    deliveryExpenses: number = DELIVERY_EXPENSES;

    totalPrice: number = 0;

    orderItems: OrderItem[] = [];

    product?: Product;

    toastMessage: string = '';

    isLoading: boolean = false;

    isCouponCodeUsed: boolean = false;

    oldPrice: number = 0;

    referrer?: Customer;

    isSearchingPlaces: boolean = false;
    isSearchingStreets: boolean = false;

    placeOptions: PlaceOption[] = [];
    streetOptions: StreetOption[] = [];

    isPlaceDropdownOpen: boolean = false;
    isStreetDropdownOpen: boolean = false;

    activePlaceIndex: number = -1;
    activeStreetIndex: number = -1;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly cartService: CartService,
        private readonly checkoutService: CheckoutService,
        protected readonly router: Router,
        public auth: AuthService,
        private readonly profileService: ProfileService,
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly analytics: AnalyticsService) {
    }

    ngOnInit(): void {
        initTE({Toast});
        this.setCustomerValues();
        this.orderItems = this.cartService.retrieveItemsFromLocalStorage();
        this.calculateTotal();
        this.initAddressAutocomplete();
        if (this.orderItems.length > 0) {
            this.analytics.beginCheckout(this.totalPrice, this.orderItems);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get nameControl(): UntypedFormControl {
        return this.order.get('name') as UntypedFormControl;
    }

    get surnameControl(): UntypedFormControl {
        return this.order.get('surname') as UntypedFormControl;
    }

    get phoneNumberControl(): UntypedFormControl {
        return this.order.get('phoneNumber') as UntypedFormControl;
    }

    get addressControl(): UntypedFormControl {
        return this.order.get('address') as UntypedFormControl;
    }


    get cityControl(): UntypedFormControl {
        return this.order.get('city') as UntypedFormControl;
    }

    get emailControl(): UntypedFormControl {
        return this.order.get('email') as UntypedFormControl;
    }

    get couponCodeControl(): UntypedFormControl {
        return this.order.get('couponCode') as UntypedFormControl;
    }

    get usePointsControl(): UntypedFormControl {
        return this.order.get('usePoints') as UntypedFormControl;
    }

    get placeInputControl(): UntypedFormControl {
        return this.order.get('placeInput') as UntypedFormControl;
    }

    get selectedPlaceIdControl(): UntypedFormControl {
        return this.order.get('selectedPlaceId') as UntypedFormControl;
    }

    get selectedPlaceEcoIdControl(): UntypedFormControl {
        return this.order.get('selectedPlaceEcoId') as UntypedFormControl;
    }

    get streetInputControl(): UntypedFormControl {
        return this.order.get('streetInput') as UntypedFormControl;
    }

    get selectedStreetIdControl(): UntypedFormControl {
        return this.order.get('selectedStreetId') as UntypedFormControl;
    }

    get selectedStreetEcoIdControl(): UntypedFormControl {
        return this.order.get('selectedStreetEcoId') as UntypedFormControl;
    }

    get manualStreetControl(): UntypedFormControl {
        return this.order.get('manualStreet') as UntypedFormControl;
    }

    get houseNumberControl(): UntypedFormControl {
        return this.order.get('houseNumber') as UntypedFormControl;
    }

    get entranceControl(): UntypedFormControl {
        return this.order.get('entrance') as UntypedFormControl;
    }

    get apartmentControl(): UntypedFormControl {
        return this.order.get('apartment') as UntypedFormControl;
    }

    get placeError(): string {
        if (this.isControlInvalid('selectedPlaceId') || this.isControlInvalid('placeInput')) {
            return 'Избери место од листата';
        }
        return '';
    }

    get streetError(): string {
        if (this.isControlInvalid('streetInput')) {
            return 'Внеси улица';
        }
        return '';
    }

    get houseNumberError(): string {
        if (this.isControlInvalid('houseNumber')) {
            return 'Внеси број';
        }
        return '';
    }

    isControlInvalid(controlName: string): boolean {
        const control = this.order.get(controlName);
        return !!control && control.invalid && (control.touched || this.submitted);
    }

    private initAddressAutocomplete(): void {
        this.placeInputControl.valueChanges
            .pipe(
                debounceTime(200),
                distinctUntilChanged(),
                tap((searchTerm: string) => {
                    if (searchTerm !== this.getSelectedPlaceName()) {
                        this.clearSelectedPlace();
                    }
                }),
                switchMap((searchTerm: string) => {
                    const normalized = (searchTerm ?? '').trim();
                    if (normalized.length < 2) {
                        this.placeOptions = [];
                        this.isSearchingPlaces = false;
                        return of([]);
                    }

                    this.isSearchingPlaces = true;
                    return this.checkoutService.searchPlaces(normalized).pipe(
                        catchError(() => of([])),
                        finalize(() => this.isSearchingPlaces = false)
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((options: PlaceOption[]) => {
                this.placeOptions = options;
                this.activePlaceIndex = options.length > 0 ? 0 : -1;
                this.isPlaceDropdownOpen = true;
            });

        this.streetInputControl.valueChanges
            .pipe(
                debounceTime(200),
                distinctUntilChanged(),
                tap((streetValue: string) => {
                    if (streetValue !== this.getSelectedStreetName()) {
                        this.selectedStreetIdControl.setValue(null, {emitEvent: false});
                        this.selectedStreetEcoIdControl.setValue(null, {emitEvent: false});
                        this.manualStreetControl.setValue(streetValue ?? '', {emitEvent: false});
                    }
                }),
                switchMap((searchTerm: string) => {
                    const normalized = (searchTerm ?? '').trim();
                    const selectedPlaceId = this.selectedPlaceIdControl.value;

                    if (!selectedPlaceId || normalized.length < 2) {
                        this.streetOptions = [];
                        this.isSearchingStreets = false;
                        return of([]);
                    }

                    this.isSearchingStreets = true;
                    return this.checkoutService.searchStreets(selectedPlaceId, normalized).pipe(
                        catchError(() => of([])),
                        finalize(() => this.isSearchingStreets = false)
                    );
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((options: StreetOption[]) => {
                this.streetOptions = options;
                this.activeStreetIndex = options.length > 0 ? 0 : -1;
                this.isStreetDropdownOpen = true;
            });

        this.houseNumberControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.buildAddressControl());
        this.entranceControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.buildAddressControl());
        this.apartmentControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.buildAddressControl());
    }

    private getSelectedPlaceName(): string {
        const selectedPlace = this.placeOptions.find(option => option.id === this.selectedPlaceIdControl.value);
        return selectedPlace?.name ?? '';
    }

    private getSelectedStreetName(): string {
        const selectedStreet = this.streetOptions.find(option => option.id === this.selectedStreetIdControl.value);
        return selectedStreet?.name ?? '';
    }

    selectPlace(place: PlaceOption): void {
        this.placeInputControl.setValue(place.name, {emitEvent: false});
        this.cityControl.setValue(place.name);
        this.selectedPlaceIdControl.setValue(place.id, {emitEvent: false});
        this.selectedPlaceEcoIdControl.setValue(place.ecoPlaceId, {emitEvent: false});
        this.isPlaceDropdownOpen = false;
        this.resetStreetState();
        this.streetInputControl.enable({emitEvent: false});
        this.streetInputControl.markAsUntouched();

        setTimeout(() => {
            const streetInput = this.elementRef.nativeElement.querySelector('[formControlName="streetInput"]') as HTMLInputElement | null;
            streetInput?.focus();
        }, 0);
    }

    selectStreet(street: StreetOption): void {
        this.streetInputControl.setValue(street.name, {emitEvent: false});
        this.selectedStreetIdControl.setValue(street.id, {emitEvent: false});
        this.selectedStreetEcoIdControl.setValue(street.ecoStreetId, {emitEvent: false});
        this.manualStreetControl.setValue('', {emitEvent: false});
        this.isStreetDropdownOpen = false;
        this.buildAddressControl();
    }

    onPlaceFocus(): void {
        this.isPlaceDropdownOpen = true;

        if (!(this.placeInputControl.value ?? '').trim()) {
            this.isSearchingPlaces = true;
            this.checkoutService.searchPlaces('')
                .pipe(
                    catchError(() => of([])),
                    finalize(() => this.isSearchingPlaces = false),
                    takeUntil(this.destroy$)
                )
                .subscribe((options: PlaceOption[]) => {
                    this.placeOptions = options;
                    this.activePlaceIndex = options.length > 0 ? 0 : -1;
                });
        }
    }

    onStreetFocus(): void {
        if (!this.selectedPlaceIdControl.value) {
            return;
        }

        this.isStreetDropdownOpen = true;

        if (!(this.streetInputControl.value ?? '').trim()) {
            this.isSearchingStreets = true;
            this.checkoutService.searchStreets(this.selectedPlaceIdControl.value, '')
                .pipe(
                    catchError(() => of([])),
                    finalize(() => this.isSearchingStreets = false),
                    takeUntil(this.destroy$)
                )
                .subscribe((options: StreetOption[]) => {
                    this.streetOptions = options;
                    this.activeStreetIndex = options.length > 0 ? 0 : -1;
                });
        }
    }

    onStreetBlur(): void {
        this.buildAddressControl();
    }

    onPlaceKeydown(event: KeyboardEvent): void {
        if (!this.isPlaceDropdownOpen || !this.placeOptions.length) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.activePlaceIndex = Math.min(this.activePlaceIndex + 1, this.placeOptions.length - 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.activePlaceIndex = Math.max(this.activePlaceIndex - 1, 0);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const option = this.placeOptions[this.activePlaceIndex] ?? this.placeOptions[0];
            if (option) {
                this.selectPlace(option);
            }
        } else if (event.key === 'Escape') {
            this.isPlaceDropdownOpen = false;
        }
    }

    onStreetKeydown(event: KeyboardEvent): void {
        if (!this.isStreetDropdownOpen || !this.streetOptions.length) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.activeStreetIndex = Math.min(this.activeStreetIndex + 1, this.streetOptions.length - 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.activeStreetIndex = Math.max(this.activeStreetIndex - 1, 0);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const option = this.streetOptions[this.activeStreetIndex] ?? this.streetOptions[0];
            if (option) {
                this.selectStreet(option);
            }
        } else if (event.key === 'Escape') {
            this.isStreetDropdownOpen = false;
        }
    }

    private buildAddressControl(): void {
        const officialStreet = this.streetInputControl.value ?? '';
        const manualStreet = this.manualStreetControl.value ?? '';
        const streetToUse = this.selectedStreetIdControl.value ? officialStreet : manualStreet;
        const houseNumber = this.houseNumberControl.value ?? '';
        const entrance = this.entranceControl.value ? `, Влез ${this.entranceControl.value}` : '';
        const apartment = this.apartmentControl.value ? `, Стан ${this.apartmentControl.value}` : '';
        this.addressControl.setValue(`${streetToUse} ${houseNumber}${entrance}${apartment}`.trim(), {emitEvent: false});
    }

    private markAddressFieldsTouched(): void {
        this.placeInputControl.markAsTouched();
        this.selectedPlaceIdControl.markAsTouched();
        this.streetInputControl.markAsTouched();
        this.houseNumberControl.markAsTouched();
    }

    private clearSelectedPlace(): void {
        this.selectedPlaceIdControl.setValue(null, {emitEvent: false});
        this.selectedPlaceEcoIdControl.setValue(null, {emitEvent: false});
        this.cityControl.setValue('', {emitEvent: false});
        this.resetStreetState();
        this.streetInputControl.disable({emitEvent: false});
    }

    private resetStreetState(): void {
        this.streetOptions = [];
        this.streetInputControl.setValue('', {emitEvent: false});
        this.selectedStreetIdControl.setValue(null, {emitEvent: false});
        this.selectedStreetEcoIdControl.setValue(null, {emitEvent: false});
        this.manualStreetControl.setValue('', {emitEvent: false});
        this.addressControl.setValue('', {emitEvent: false});
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('[data-autocomplete="place"]')) {
            this.isPlaceDropdownOpen = false;
        }
        if (!target?.closest('[data-autocomplete="street"]')) {
            this.isStreetDropdownOpen = false;
        }
    }

    setCustomerValues() {
        return this.auth.user$.subscribe(
            (user) => {
                // Guests have no profile - this used to request /customers/email/undefined.
                if (!user?.email) {
                    return;
                }
                this.profileService.getCustomerByEmail(user.email).subscribe((customer) => {
                    this.nameControl.setValue(customer.name)
                    this.surnameControl.setValue(customer.surname)
                    this.emailControl.setValue(customer.email)
                    this.addressControl.setValue(customer.address)
                    this.cityControl.setValue(customer.city)
                    this.phoneNumberControl.setValue(customer.phoneNumber)
                    this.customer = customer;
                })
            })
    }

    calculateTotal() {
        this.totalPrice = 0;
        this.orderPrice = 0;

        for (const orderItem of this.orderItems) {
            if (orderItem.product && orderItem.product.price && orderItem.quantity) {
                this.orderPrice += orderItem.product.price * orderItem.quantity;
            }
        }
        if (this.orderPrice < FREE_DELIVERY_THRESHOLD) {
            this.totalPrice += this.deliveryExpenses;
        }

        this.totalPrice += this.orderPrice;
    }

    submitOrder(): void {
        this.isLoading = true;

        const orderCreationRequest: OrderCreationRequest = {
            customer: {
                id: this.customer?.id,
                name: this.nameControl.value,
                surname: this.surnameControl.value,
                phoneNumber: this.phoneNumberControl.value,
                address: this.addressControl.value,
                city: this.cityControl.value,
                email: this.emailControl.value,
                totalPoints: this.customer?.totalPoints ?? 0,
                currentPoints: this.customer?.currentPoints ?? 0,
                referrer: this.referrer,
                couponCode: this.customer?.couponCode,
            },
            couponCode: this.referrer?.couponCode ? `${this.referrer.couponCode}` : "",
            orderItems: this.orderItems,
            totalPrice: this.totalPrice,
            usePoints: this.usePointsControl.value,
            orderPrice: this.orderPrice,
            isCouponCodeUsed: this.isCouponCodeUsed
        };

        this.checkoutService.submitOrder(orderCreationRequest)
            .subscribe({
                next: (order) => {
                    this.analytics.purchase(String(order.id ?? Date.now()), this.totalPrice, this.orderItems);
                    this.openToast("Вашата нарачка е пратена");
                    // The cart is cleared below - hand the order over to the confirmation page first.
                    const successState: OrderSuccessState = {
                        productIds: [...new Set(this.orderItems.map(item => item.product?.id).filter((id): id is number => id != null))],
                        productNames: this.orderItems.map(item => item.product?.name ?? '').filter(name => !!name),
                        email: this.emailControl.value || null
                    };
                    this.cartService.clearLocalStorage();
                    fbq('track', 'Purchase', {
                        value: this.totalPrice,
                        currency: 'MKD',
                        contents: this.orderItems.map(orderItem => ({
                            id: orderItem.product?.id,
                            quantity: orderItem.quantity,
                        })),
                        content_type: 'product',
                    });
                    setTimeout(() => {
                        this.router.navigateByUrl('/uspesna-naracka', {state: successState});
                    }, 1500);
                },
                error: () => {
                    this.openToast("Грешка при праќање на нарачка");
                    this.isLoading = false;
                },
                complete: () => this.isLoading = false
            });
    }

    openToast(message: string) {
        const toast = new Toast(document.getElementById("toast"));
        this.toastMessage = message;
        toast.show();
    }

    async openConfirmationModal(): Promise<void> {
        this.submitted = true;
        this.buildAddressControl();
        this.markAddressFieldsTouched();

        if (this.couponCodeControl.value && !this.isCouponCodeUsed) {
            const isCouponApplied = await this.applyCoupon();
            if (!isCouponApplied) {
                this.isLoading = false;
                return;
            }
        }
        if (this.order.status === 'VALID' && this.orderItems.length > 0) {
            this.analytics.addShippingInfo(this.totalPrice, this.orderItems);
            const confirmationModal = new Modal(document.getElementById("confirmationModal"));
            confirmationModal.show();
        } else {
            if (this.order.status !== 'VALID') {
                this.openToast('Пополнете ја формата');
            } else {
                this.openToast('Додади производи во кошничката');
            }
        }
    }

    applyCoupon(): Promise<boolean> {
        this.isLoading = true;
        const couponCode = this.couponCodeControl.value;

        return new Promise((resolve) => {
            if (!couponCode) {
                this.openToast('Внеси купон код');
                this.isLoading = false;
                resolve(false);
                return;
            }

            if (couponCode === this.customer?.couponCode) {
                this.openToast("Не можеш да го користиш сопствениот купон код");
                this.isLoading = false;
                return;
            }

            const couponCodeRequest: CouponCodeRequest = {
                couponCode: couponCode,
                totalPrice: this.totalPrice
            };

            this.checkoutService.applyCoupon(couponCodeRequest).subscribe({
                next: (response) => {
                    if (response.totalPrice !== this.totalPrice) {
                        this.oldPrice = this.totalPrice;
                        this.totalPrice = response.totalPrice;
                        this.isCouponCodeUsed = true;
                        this.referrer = response.referrer;
                        resolve(true);
                    } else {
                        this.isCouponCodeUsed = false;
                        this.openToast(`Невалиден купон код: ${couponCode}`);
                        resolve(false);
                    }
                },
                error: (e) => {
                    this.openToast(`Невалиден купон код: ${e.message}`);
                    resolve(false);
                },
                complete: () => {
                    this.isLoading = false;
                }
            });
        });
    }

    handleCheckboxChange(isChecked: boolean): void {
        this.usePointsControl.setValue(isChecked);
    }

    protected readonly goToUrl = goToUrl;
}